import { Link } from '@inertiajs/react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import { TopoContours } from '@/components/patterns'
import ShareButtons from '@/components/ShareButtons'
import FundraisingProgressBar from '@/components/event-show/FundraisingProgressBar'
import { formatDressCode, formatTime } from '@/lib/format'
import {
  Calendar,
  Clock,
  MapPin,
  Tag,
  CommitteeSection,
  CountdownBlock,
  DetailIconRow,
  DonationPanel,
  EntertainmentSection,
  EventNeedsSection,
  HashtagsSection,
  HonoreesSection,
  HostsSection,
  IndustriesSection,
  CausesSection,
  MobileDonateBar,
  OrganizationCard,
  PurchaseActions,
  SaveEventButton,
  ShareAndCalendar,
  SponsorsSection,
  VenueMap,
  buildAddressQuery,
  buildLocationLines,
  formatDate,
  formatPrice,
  hasPurchaseActionContent,
  useCountdown,
} from './shared'
import type { DetailOrganization, Event } from '@/types'

type Props = {
  event: Event
  organization: DetailOrganization | null
  isFollowing: boolean
  isSaved: boolean
  googleMapsApiKey: string | null
  brandFree: boolean
  eventUrl: string
  isPreview: boolean
}

export default function GalleryTemplate({
  event,
  organization,
  isFollowing,
  isSaved,
  googleMapsApiKey,
  brandFree,
  eventUrl,
  isPreview,
}: Props) {
  const dressCode = formatDressCode(event.dress_code)
  const startTime = formatTime(event.start_time)
  const endTime = formatTime(event.end_time)
  const timeRange =
    startTime && endTime ? `${startTime} – ${endTime}` : startTime || endTime || null
  const eventDate = event.date ?? ''
  const countdown = useCountdown(eventDate, event.start_time)
  const showCountdown = event.countdown_timer && countdown !== null
  const hasImage = !!event.cover_image_url
  const locationLines = buildLocationLines(event)
  const addressQuery = buildAddressQuery(event)
  const showMap = Boolean(googleMapsApiKey && locationLines.length > 0 && addressQuery)
  const isFundraiser = event.page_kind === 'fundraiser'
  const showPurchaseCard = Boolean(event.starting_ticket_price) || hasPurchaseActionContent(event, organization)

  if (isFundraiser) {
    return (
      <>
        <div className="mx-auto max-w-7xl px-4 pt-10 pb-24 sm:px-6 lg:px-8 lg:pb-10">
          <div className="mb-8">
            <h1 className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl">{event.title}</h1>
            {organization && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-lg text-muted-foreground">
                by{' '}
                {brandFree ? (
                  <span className="font-medium text-foreground">{organization.name}</span>
                ) : (
                  <Link href={`/o/${organization.slug}`} className="font-medium text-foreground underline-offset-4 hover:underline">
                    {organization.name}
                  </Link>
                )}
                {organization.verified && <VerifiedBadge className="size-4 text-foreground" />}
              </p>
            )}
          </div>

          {/* Top: banner left + donate right */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-stretch">
            <div className="space-y-6">
              {hasImage ? (
                <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl">
                  <img src={event.cover_image_url!} alt="" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-linear-to-br from-[#F3722B] to-[#F7961E]">
                  <TopoContours />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
                </div>
              )}
              <FundraisingProgressBar event={event} />
            </div>

            <div className="flex flex-col gap-6">
              <div id="donate" className="scroll-mt-24 rounded-2xl bg-muted/30 p-6 ring-1 ring-black/5">
                <DonationPanel event={event} organization={organization} isPreview={isPreview} />
              </div>

              {event.status !== 'draft' && !brandFree && !isPreview && (
                <div className="max-w-xs">
                  <ShareButtons eventId={event.id} title={event.title} url={eventUrl} label="Share Fundraiser" />
                </div>
              )}
            </div>
          </div>

          {/* About + causes */}
          <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-start">
            <div className="space-y-8 [&>*+*]:border-t [&>*+*]:border-black/5 [&>*+*]:pt-8">
              {event.description && (
                <div>
                  <h2 className="mb-4 text-xl font-semibold">About this fundraiser</h2>
                  <p className="max-w-[65ch] whitespace-pre-wrap text-pretty">{event.description}</p>
                </div>
              )}
            </div>
            <div className="space-y-8 [&>*+*]:border-t [&>*+*]:border-black/5 [&>*+*]:pt-8">
              {organization && <CausesSection causes={organization.causes} />}
              {organization && <IndustriesSection industries={organization.industries} />}
            </div>
          </div>

          {/* Organization card at the bottom */}
          <div className="mt-14">
            <OrganizationCard
              event={event}
              organization={organization}
              isFollowing={isFollowing}
              brandFree={brandFree}
              isPreview={isPreview}
            />
          </div>
        </div>

        <MobileDonateBar event={event} organization={organization} isPreview={isPreview} />
      </>
    )
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-8 empty:mb-0">
        <FundraisingProgressBar event={event} />
      </div>
      {/* Top: banner left + title/org/details/purchase right */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-stretch">
        <div className="lg:h-full">
          {hasImage ? (
            <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full w-full overflow-hidden rounded-2xl">
              <img src={event.cover_image_url!} alt="" className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full w-full overflow-hidden rounded-2xl bg-linear-to-br from-[#F3722B] to-[#F7961E]">
              <TopoContours />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl">{event.title}</h1>
            {organization && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-lg text-muted-foreground">
                {brandFree ? (
                  <span className="font-medium text-foreground">{organization.name}</span>
                ) : (
                  <Link href={`/o/${organization.slug}`} className="font-medium text-foreground underline-offset-4 hover:underline">
                    {organization.name}
                  </Link>
                )}
                {organization.verified && <VerifiedBadge className="size-4 text-foreground" />}
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Calendar className="size-4 text-primary" />
              </div>
              <p className="font-semibold">{formatDate(eventDate)}</p>
            </div>

            {timeRange && (
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Clock className="size-4 text-primary" />
                </div>
                <p className="font-semibold">{timeRange}</p>
              </div>
            )}

            {locationLines.length > 0 && (
              <DetailIconRow icon={<MapPin className="size-4 text-primary" />}>
                <p className="font-semibold">{locationLines[0]}</p>
                {locationLines.slice(1).map((line, index) => (
                  <p key={`${line}-${index}`} className="text-base text-muted-foreground">{line}</p>
                ))}
              </DetailIconRow>
            )}

            {dressCode && (
              <DetailIconRow icon={<Tag className="size-4 text-primary" />}>
                <p className="text-base font-medium text-muted-foreground">Dress Code</p>
                <p className="font-semibold">{dressCode}</p>
              </DetailIconRow>
            )}
          </div>

          {showPurchaseCard && (
            <div className="rounded-xl border border-black/5 bg-card p-4">
              {event.starting_ticket_price && (
                <>
                  <p className="text-base font-medium text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-semibold tabular-nums">{formatPrice(event.starting_ticket_price)}</p>
                </>
              )}
              <PurchaseActions event={event} organization={organization} isPreview={isPreview} />
            </div>
          )}

          <div className="space-y-3">
            <SaveEventButton event={event} isSaved={isSaved} brandFree={brandFree} isPreview={isPreview} />
            <ShareAndCalendar event={event} brandFree={brandFree} eventUrl={eventUrl} isPreview={isPreview} />
          </div>
        </div>
      </div>

      {/* Sections + About — each column flows independently so heights don't couple */}
      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-start">
        <div className="space-y-8 [&>*+*]:border-t [&>*+*]:border-black/5 [&>*+*]:pt-8">
          <SponsorsSection event={event} isPreview={isPreview} />
          <HonoreesSection honorees={event.honorees} />
          <EntertainmentSection entertainment={event.entertainment} />
          <CommitteeSection members={event.committee_members} />
          <HostsSection hosts={event.hosts} />
          {showCountdown && countdown && (
            <div>
              <h2 className="mb-4 text-xl font-semibold">Countdown</h2>
              <CountdownBlock countdown={countdown} className="flex justify-center gap-10 rounded-2xl bg-primary/5 p-8 text-center" />
            </div>
          )}
        </div>
        <div className="space-y-8 [&>*+*]:border-t [&>*+*]:border-black/5 [&>*+*]:pt-8">
          {event.description && (
            <div>
              <h2 className="mb-4 text-xl font-semibold">About the Event</h2>
              <p className="max-w-[65ch] whitespace-pre-wrap text-pretty">{event.description}</p>
            </div>
          )}
          <HashtagsSection hashtags={event.hashtags} />
          {organization && <CausesSection causes={organization.causes} />}
          {organization && <IndustriesSection industries={organization.industries} />}
          <EventNeedsSection event={event} />
          {showMap && (
            <VenueMap event={event} locationLines={locationLines} addressQuery={addressQuery} apiKey={googleMapsApiKey} />
          )}
        </div>
      </div>

      {/* Organization card at the bottom */}
      <div className="mt-14">
        <OrganizationCard
          event={event}
          organization={organization}
          isFollowing={isFollowing}
          brandFree={brandFree}
          isPreview={isPreview}
        />
      </div>
    </div>
  )
}
